import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { useState } from 'react';
import LoadingButton from '@/components/Buttons/LoadingButton';
import { deleteUser as deleteUserInDb, disableUserAccount } from '@/services/UserService';
import { errorSnackbar, successSnackbar } from '@/utils/snackbar';

interface Props {
    userId: number;
    email: string;
    onClose(): void;
}

export default function DeleteUserDialog({ userId, email, onClose }: Props) {
    const queryClient = useQueryClient();
    const [isDeleting, setIsDeleting] = useState(false);

    const handleDeleteUser = async () => {
        setIsDeleting(true);

        try {
            await deleteUserInDb(userId, email);
            await disableUserAccount(email);

            await queryClient.invalidateQueries({ queryKey: ['users'] });
            successSnackbar('User deleted successfully.');
            onClose();
        } catch (error: unknown) {
            console.error('Error deleting user:', error);
            const errorMessage = isAxiosError(error)
                ? (error.response?.data as string)
                : 'Failed to delete user. Please try again.';
            errorSnackbar(errorMessage);
        } finally {
            setIsDeleting(false);
        }
    };

    return (
        <Dialog
            open
            onClose={(_event, reason) => {
                if (reason === 'backdropClick' && isDeleting) return;
                onClose();
            }}
        >
            <DialogTitle>Delete User</DialogTitle>
            <DialogContent>
                <Typography variant="body2" sx={{ mb: 2 }}>
                    Are you sure you want to delete this user?
                </Typography>
                <Typography variant="body2">{email}</Typography>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={isDeleting}>
                    Cancel
                </Button>
                <LoadingButton
                    onClick={handleDeleteUser}
                    loading={isDeleting}
                    color="error"
                    variant="contained"
                >
                    Confirm
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
}
